import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';

export interface AdminNoteModalProps {
  open: boolean;
  title: string;
  description?: string;
  placeholder?: string;
  confirmLabel?: string;
  confirmTone?: 'primary' | 'danger';
  requireNote?: boolean; // true면 메모가 비어 있으면 확인 비활성화
  loading?: boolean;
  onConfirm: (note: string) => void; // 확인 시 입력 메모 전달(trim된 문자열)
  onCancel: () => void;
}

/**
 * 관리자 메모 입력용 인라인 모달.
 * window.prompt 를 대체 — 멀티라인 입력, 검증(required), 키보드(Esc/⌘+Enter), 로딩 상태를 지원한다.
 * 상위 모달(z-50) 위에 표시되도록 z-[60] 사용.
 */
const AdminNoteModal: React.FC<AdminNoteModalProps> = ({
  open,
  title,
  description,
  placeholder,
  confirmLabel = '확인',
  confirmTone = 'primary',
  requireNote = false,
  loading = false,
  onConfirm,
  onCancel,
}) => {
  const [note, setNote] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // 열릴 때마다 입력 초기화 + 포커스
  useEffect(() => {
    if (open) {
      setNote('');
      // 다음 틱에 포커스 (렌더 직후)
      const t = setTimeout(() => textareaRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);

  if (!open) return null;

  const trimmed = note.trim();
  const confirmDisabled = loading || (requireNote && trimmed.length === 0);

  const submit = () => {
    if (confirmDisabled) return;
    onConfirm(trimmed);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (!loading) onCancel();
    } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      submit();
    }
  };

  const confirmClasses =
    confirmTone === 'danger'
      ? 'bg-red-600 hover:bg-red-700'
      : 'bg-gray-900 hover:bg-gray-700';

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
      onMouseDown={(e) => {
        // 배경 클릭 시 닫기 (로딩 중에는 무시)
        if (e.target === e.currentTarget && !loading) onCancel();
      }}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full"
        onKeyDown={handleKeyDown}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-900">{title}</h3>
          <button
            onClick={onCancel}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-40"
            aria-label="닫기"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4">
          {description && <p className="text-sm text-gray-500 mb-2">{description}</p>}
          <textarea
            ref={textareaRef}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={placeholder}
            rows={4}
            maxLength={500}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 resize-none"
          />
          <div className="flex items-center justify-between mt-1">
            <span className="text-xs text-gray-400">
              {requireNote ? '필수 입력' : '선택 입력'} · ⌘/Ctrl+Enter로 확인
            </span>
            <span className="text-xs text-gray-400">{note.length}/500</span>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-200">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          >
            취소
          </button>
          <button
            onClick={submit}
            disabled={confirmDisabled}
            className={`px-4 py-2 text-sm rounded-lg text-white disabled:opacity-40 ${confirmClasses}`}
          >
            {loading ? '처리 중…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminNoteModal;
